/**
 * Advanced Search Page
 * Provides detailed filtering for adventures including tags, categories, and ratings
 */

import { useState, useEffect } from 'react'
import { usePageTitle } from '@components/layout/MainLayout'
import '../../adventures/components/editor/styles/TagsPanel.css'
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Grid,
  Chip,
  Slider,
  TextField,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  IconButton,
  ListItemText,
  Tooltip,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material'
import {
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  FilterAlt as FilterIcon,
  Clear as ClearIcon,
  Save as SaveIcon,
  History as HistoryIcon,
  Delete as DeleteIcon,
  Explore as ExploreIcon,
  People as PeopleIcon,
  Groups as GroupsIcon,
  CollectionsBookmark as CollectionsIcon,
} from '@mui/icons-material'
import { useTags } from '@contexts/TagsContext'
import { TagType, AdventureMainCategory } from '@features/adventures/types/adventure-editor.types'
import { useGlobalSearch } from '@features/search/hooks/useGlobalSearch'
import { SearchEntityType } from '@api/types/search'
import { AdventureResultCard } from '@features/search/components/AdventureResultCard'
import { UserResultCard } from '@features/search/components/UserResultCard'
import { GroupResultCard } from '@features/search/components/GroupResultCard'
import { CollectionResultCard } from '@features/search/components/CollectionResultCard'

interface AdvancedSearchFilters {
  keyword: string
  entityTypes: number // Bitwise flags: 1=Adventure, 2=User, 4=Group, 8=Collections
  categories: number[]
  interestTags: string[]
  adventureTypeTags: string[]
  minRating: number
  maxRating: number
}

interface SavedSearch {
  id: string
  name: string
  filters: AdvancedSearchFilters
  savedAt: string
}

// Map AdventureMainCategory enum to display names
const ADVENTURE_CATEGORIES = [
  { value: AdventureMainCategory.TraditionalGeoCaches, label: 'Traditional GeoCaches' },
  { value: AdventureMainCategory.NatureAdventure, label: 'Nature Adventure' },
  { value: AdventureMainCategory.HistoricalAdventure, label: 'Historical Adventure' },
  { value: AdventureMainCategory.UrbanExploration, label: 'Urban Exploration' },
  { value: AdventureMainCategory.RiddleQuest, label: 'Riddle Quest' },
  { value: AdventureMainCategory.SupportingBusiness, label: 'Supporting Business' },
]

export function AdvancedSearchPage() {
  usePageTitle('Advanced Search')
  const { isLoading: tagsLoading, getCategoriesByType } = useTags()

  const [filters, setFilters] = useState<AdvancedSearchFilters>({
    keyword: '',
    entityTypes: SearchEntityType.Adventure | SearchEntityType.User | SearchEntityType.Group | SearchEntityType.Collections, // Default to All (15)
    categories: [],
    interestTags: [],
    adventureTypeTags: [],
    minRating: 0,
    maxRating: 5,
  })

  const [ratingRange, setRatingRange] = useState<number[]>([0, 5])
  const [hasSearched, setHasSearched] = useState(false)

  // Submitted search parameters - only updated when user clicks search button
  // This prevents auto-searching on every keystroke
  const [submittedSearch, setSubmittedSearch] = useState<{
    keyword: string
    entityTypes: number
    tagIds: string[]
  } | null>(null)

  // Search hook - uses submitted params, not live filter values
  const { data: searchData, isLoading: isSearching } = useGlobalSearch({
    query: submittedSearch?.keyword || '',
    entityType: submittedSearch?.entityTypes === 15 ? undefined : submittedSearch?.entityTypes,
    tagIds: submittedSearch?.tagIds,
    enabled: hasSearched && submittedSearch !== null,
  })

  // State for controlling which accordion is expanded in each section
  const [expandedInterestCategory, setExpandedInterestCategory] = useState<string | null>(null)
  const [expandedAdventureTypeCategory, setExpandedAdventureTypeCategory] = useState<string | null>(null)

  // Saved searches state
  const [savedSearches, setSavedSearches] = useState<SavedSearch[]>([])
  const [saveDialogOpen, setSaveDialogOpen] = useState(false)
  const [searchName, setSearchName] = useState('')
  const [savedSearchesMenuAnchor, setSavedSearchesMenuAnchor] = useState<null | HTMLElement>(null)

  // Get tag categories filtered by type
  const interestCategories = getCategoriesByType(TagType.Interests)
  const adventureTypeCategories = getCategoriesByType(TagType.Adventures)

  // Update rating range when slider changes
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      minRating: ratingRange[0],
      maxRating: ratingRange[1],
    }))
  }, [ratingRange])

  // Load saved searches from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('savedAdvancedSearches')
    if (saved) {
      try {
        setSavedSearches(JSON.parse(saved))
      } catch (e) {
        console.error('Failed to load saved searches:', e)
      }
    }
  }, [])

  // Save current search
  const handleSaveSearch = () => {
    if (!searchName.trim()) return

    const newSearch: SavedSearch = {
      id: Date.now().toString(),
      name: searchName.trim(),
      filters: { ...filters },
      savedAt: new Date().toISOString(),
    }

    const updated = [newSearch, ...savedSearches]
    setSavedSearches(updated)
    localStorage.setItem('savedAdvancedSearches', JSON.stringify(updated))

    setSearchName('')
    setSaveDialogOpen(false)
  }

  // Load a saved search
  const handleLoadSearch = (search: SavedSearch) => {
    setFilters(search.filters)
    setRatingRange([search.filters.minRating, search.filters.maxRating])
    setSavedSearchesMenuAnchor(null)
  }

  // Delete a saved search
  const handleDeleteSearch = (searchId: string) => {
    const updated = savedSearches.filter(s => s.id !== searchId)
    setSavedSearches(updated)
    localStorage.setItem('savedAdvancedSearches', JSON.stringify(updated))
  }

  // Convert bitwise flags to array for ToggleButtonGroup
  const getSelectedEntityTypes = (flags: number): number[] => {
    const selected: number[] = []
    if (flags & SearchEntityType.Adventure) selected.push(SearchEntityType.Adventure)
    if (flags & SearchEntityType.User) selected.push(SearchEntityType.User)
    if (flags & SearchEntityType.Group) selected.push(SearchEntityType.Group)
    if (flags & SearchEntityType.Collections) selected.push(SearchEntityType.Collections)
    return selected
  }

  // Handle multi-select entity type toggle
  const handleEntityTypeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newTypes: number[]
  ) => {
    // Prevent deselecting all - at least one must be selected
    if (newTypes.length === 0) return

    // Combine selected types using bitwise OR
    const combined = newTypes.reduce((acc, type) => acc | type, 0)
    setFilters(prev => ({ ...prev, entityTypes: combined }))
  }

  // Check if adventures or collections are being searched
  const showAdventureFilters = (filters.entityTypes & (SearchEntityType.Adventure | SearchEntityType.Collections)) !== 0

  // Check if users or groups are being searched
  const showUserGroupFilters = (filters.entityTypes & (SearchEntityType.User | SearchEntityType.Group)) !== 0

  // Check if adventures specifically are being searched (for adventure-only filters)
  const showAdventureOnlyFilters = (filters.entityTypes & SearchEntityType.Adventure) !== 0

  const handleCategoryToggle = (categoryValue: number) => {
    setFilters(prev => ({
      ...prev,
      categories: prev.categories.includes(categoryValue)
        ? prev.categories.filter(c => c !== categoryValue)
        : [...prev.categories, categoryValue],
    }))
  }

  const handleInterestTagToggle = (tagId: string) => {
    setFilters(prev => ({
      ...prev,
      interestTags: prev.interestTags.includes(tagId)
        ? prev.interestTags.filter(id => id !== tagId)
        : [...prev.interestTags, tagId],
    }))
  }

  const handleAdventureTypeTagToggle = (tagId: string) => {
    setFilters(prev => ({
      ...prev,
      adventureTypeTags: prev.adventureTypeTags.includes(tagId)
        ? prev.adventureTypeTags.filter(id => id !== tagId)
        : [...prev.adventureTypeTags, tagId],
    }))
  }

  const handleClearFilters = () => {
    setFilters({
      keyword: '',
      entityTypes: SearchEntityType.Adventure | SearchEntityType.User | SearchEntityType.Group | SearchEntityType.Collections,
      categories: [],
      interestTags: [],
      adventureTypeTags: [],
      minRating: 0,
      maxRating: 5,
    })
    setRatingRange([0, 5])
  }

  const handleSearch = () => {
    // Build tag IDs based on entity types being searched
    const tagIds: string[] = []

    // Include interest tags if searching users/groups
    if (filters.entityTypes & (SearchEntityType.User | SearchEntityType.Group)) {
      tagIds.push(...filters.interestTags)
    }

    // Include adventure type tags if searching adventures
    if (filters.entityTypes & SearchEntityType.Adventure) {
      tagIds.push(...filters.adventureTypeTags)
    }

    // Set submitted search params - this triggers the actual search
    setSubmittedSearch({
      keyword: filters.keyword,
      entityTypes: filters.entityTypes,
      tagIds,
    })
    setHasSearched(true)
  }

  // Get filtered results for all entity types
  const getFilteredResults = () => {
    if (!searchData) return { adventures: [], users: [], groups: [], collections: [] }

    let adventures = [...(searchData.adventures || [])]
    const users = [...(searchData.users || [])]
    const groups = [...(searchData.groups || [])]
    const collections = [...(searchData.collections || [])]

    // Client-side category filtering for adventures (if not handled by API)
    if (filters.categories.length > 0) {
      adventures = adventures.filter(adv => filters.categories.includes(adv.mainCategory))
    }

    // Client-side rating filtering for adventures (if not handled by API)
    if (filters.minRating > 0 || filters.maxRating < 5) {
      adventures = adventures.filter(adv => {
        const rating = adv.averageRating || 0
        return rating >= filters.minRating && rating <= filters.maxRating
      })
    }

    return { adventures, users, groups, collections }
  }

  const filteredResults = getFilteredResults()

  // Calculate total result count
  const totalResultCount =
    (filters.entityTypes & SearchEntityType.Adventure ? filteredResults.adventures.length : 0) +
    (filters.entityTypes & SearchEntityType.User ? filteredResults.users.length : 0) +
    (filters.entityTypes & SearchEntityType.Group ? filteredResults.groups.length : 0) +
    (filters.entityTypes & SearchEntityType.Collections ? filteredResults.collections.length : 0)

  const hasActiveFilters =
    filters.keyword !== '' ||
    filters.entityTypes !== 15 || // Not "All" selected
    filters.categories.length > 0 ||
    filters.interestTags.length > 0 ||
    filters.adventureTypeTags.length > 0 ||
    filters.minRating > 0 ||
    filters.maxRating < 5

  // Get dynamic search button text based on selected entity types
  const getSearchButtonText = () => {
    const types: string[] = []
    if (filters.entityTypes & SearchEntityType.Adventure) types.push('Adventures')
    if (filters.entityTypes & SearchEntityType.User) types.push('Users')
    if (filters.entityTypes & SearchEntityType.Group) types.push('Groups')
    if (filters.entityTypes & SearchEntityType.Collections) types.push('Collections')

    if (types.length === 4) return 'Search All'
    if (types.length === 1) return `Search ${types[0]}`
    return 'Search Selected'
  }

  // Get entity type names for display
  const getSelectedEntityTypeNames = () => {
    const types: string[] = []
    if (filters.entityTypes & SearchEntityType.Adventure) types.push('Adventures')
    if (filters.entityTypes & SearchEntityType.User) types.push('Users')
    if (filters.entityTypes & SearchEntityType.Group) types.push('Groups')
    if (filters.entityTypes & SearchEntityType.Collections) types.push('Collections')
    return types.join(', ')
  }

  // Get tag chip style with category color
  const getTagChipStyle = (categoryColor?: string, isSelected?: boolean) => {
    if (isSelected) {
      return {
        backgroundColor: categoryColor || '#1976d2',
        color: 'white',
        borderColor: categoryColor || '#1976d2',
      }
    }
    return {
      borderColor: categoryColor || '#ccc',
      color: categoryColor || '#666',
    }
  }

  return (
    <Container
      maxWidth="lg"
      sx={{
        py: { xs: 1, sm: 2, md: 4 },
        px: { xs: '5px', sm: 2, md: 3 }
      }}
    >
      {/* Compact Header - Single Line on Mobile */}
      <Box
        sx={{
          mb: { xs: 1, sm: 2, md: 3 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1
        }}
      >
        {/* Left: Icon + Title */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 }, minWidth: 0 }}>
          <FilterIcon
            sx={{
              fontSize: { xs: 24, sm: 32, md: 40 },
              color: 'primary.main',
              flexShrink: 0
            }}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h6"
              sx={{
                fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
                lineHeight: 1.2,
                mb: 0
              }}
            >
              Advanced Search
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: { xs: 'none', sm: 'block' },
                fontSize: { sm: '0.75rem', md: '0.875rem' }
              }}
            >
              Find the perfect adventure
            </Typography>
          </Box>
        </Box>

        {/* Right: Action Buttons */}
        <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
          <Tooltip title="Load Saved Search">
            <IconButton
              onClick={(e) => setSavedSearchesMenuAnchor(e.currentTarget)}
              color="primary"
              disabled={savedSearches.length === 0}
              size="small"
            >
              <HistoryIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Save Current Search">
            <IconButton
              onClick={() => setSaveDialogOpen(true)}
              color="primary"
              disabled={!hasActiveFilters}
              size="small"
            >
              <SaveIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Search Form */}
      <Paper sx={{
        p: { xs: 1, sm: 2, md: 3 },
        mx: { xs: 0 }
      }}>
        <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
          {/* Entity Type Selection */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Search For
            </Typography>
            <ToggleButtonGroup
              value={getSelectedEntityTypes(filters.entityTypes)}
              onChange={handleEntityTypeChange}
              size="small"
              sx={{ flexWrap: 'wrap', gap: 0.5 }}
            >
              <ToggleButton value={SearchEntityType.Adventure} sx={{ px: { xs: 1, sm: 2 } }}>
                <ExploreIcon sx={{ mr: { xs: 0, sm: 0.5 } }} fontSize="small" />
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Adventures</Box>
              </ToggleButton>
              <ToggleButton value={SearchEntityType.User} sx={{ px: { xs: 1, sm: 2 } }}>
                <PeopleIcon sx={{ mr: { xs: 0, sm: 0.5 } }} fontSize="small" />
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Users</Box>
              </ToggleButton>
              <ToggleButton value={SearchEntityType.Group} sx={{ px: { xs: 1, sm: 2 } }}>
                <GroupsIcon sx={{ mr: { xs: 0, sm: 0.5 } }} fontSize="small" />
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Groups</Box>
              </ToggleButton>
              <ToggleButton value={SearchEntityType.Collections} sx={{ px: { xs: 1, sm: 2 } }}>
                <CollectionsIcon sx={{ mr: { xs: 0, sm: 0.5 } }} fontSize="small" />
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Collections</Box>
              </ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Select one or more types to search
            </Typography>
          </Grid>

          {/* Keyword Search */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Search Keywords"
              placeholder="Enter adventure name, description, or location..."
              value={filters.keyword}
              onChange={(e) => setFilters(prev => ({ ...prev, keyword: e.target.value }))}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          </Grid>

          {/* Adventure Categories - Only show for Adventures/Collections */}
          {showAdventureFilters && (
            <Grid item xs={12}>
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">
                    Adventure Categories
                    {filters.categories.length > 0 && (
                      <Chip
                        label={filters.categories.length}
                        size="small"
                        color="primary"
                        sx={{ ml: 2 }}
                      />
                    )}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {ADVENTURE_CATEGORIES.map((category) => (
                      <Chip
                        key={category.value}
                        label={category.label}
                        onClick={() => handleCategoryToggle(category.value)}
                        color={filters.categories.includes(category.value) ? 'primary' : 'default'}
                        variant={filters.categories.includes(category.value) ? 'filled' : 'outlined'}
                        sx={{ mb: 1 }}
                      />
                    ))}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            </Grid>
          )}

          {/* Interest Tags - Only for Users/Groups */}
          {showUserGroupFilters && (
          <Grid item xs={12}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 1 }}>
                Interest Tags
                {filters.interestTags.length > 0 && (
                  <Chip
                    label={filters.interestTags.length}
                    size="small"
                    color="primary"
                    sx={{ ml: 2 }}
                  />
                )}
              </Typography>
              {tagsLoading ? (
                <Typography color="text.secondary">Loading tags...</Typography>
              ) : interestCategories.length === 0 ? (
                <Alert severity="info">No interest tags available</Alert>
              ) : (
                <>
                  {interestCategories
                    .filter((c) => c.isActive && c.tags && c.tags.some((t) => t.isActive))
                    .map((category) => (
                      <Accordion
                        key={category.id}
                        expanded={expandedInterestCategory === category.id}
                        onChange={(_, isExpanded) =>
                          setExpandedInterestCategory(isExpanded ? category.id : null)
                        }
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon sx={{ fontSize: 20 }} />}
                          sx={{
                            minHeight: '40px',
                            '&.Mui-expanded': { minHeight: '40px' },
                            '& .MuiAccordionSummary-content': {
                              my: 0.75,
                              '&.Mui-expanded': { my: 0.75 },
                            },
                          }}
                        >
                          <Box className="category-header">
                            <Box className="category-header-left">
                              <span className="category-icon" style={{ fontSize: '16px' }}>
                                {category.icon || '📁'}
                              </span>
                              <span
                                style={{ color: category.color, fontWeight: 500, fontSize: '0.875rem' }}
                              >
                                {category.displayName}
                              </span>
                            </Box>
                            <Box className="category-header-right">
                              <span
                                className="tag-count-badge"
                                style={{
                                  backgroundColor: category.color,
                                  color: 'white',
                                  fontSize: '0.7rem',
                                  padding: '2px 6px',
                                  minWidth: '20px',
                                  height: '18px',
                                }}
                              >
                                {category.tags?.filter((t) => t.isActive).length || 0}
                              </span>
                            </Box>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          {category.description && (
                            <Typography
                              variant="caption"
                              sx={{ mb: 2, display: 'block', color: '#666' }}
                            >
                              {category.description}
                            </Typography>
                          )}
                          <Box className="tag-chips-container">
                            {category.tags
                              ?.filter((t) => t.isActive)
                              .sort((a, b) => {
                                const orderDiff = a.sortOrder - b.sortOrder
                                return orderDiff !== 0
                                  ? orderDiff
                                  : a.displayName.localeCompare(b.displayName)
                              })
                              .map((tag) => {
                                const isSelected = filters.interestTags.includes(tag.id)
                                return (
                                  <Chip
                                    key={tag.id}
                                    label={
                                      <>
                                        {tag.icon && <span className="tag-icon">{tag.icon}</span>}
                                        <span className="tag-label">{tag.displayName}</span>
                                      </>
                                    }
                                    onClick={() => handleInterestTagToggle(tag.id)}
                                    variant={isSelected ? 'filled' : 'outlined'}
                                    size="small"
                                    sx={getTagChipStyle(category.color, isSelected)}
                                    title={tag.description}
                                    className="tag-chip"
                                  />
                                )
                              })}
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                </>
              )}
            </Box>
          </Grid>
          )}

          {/* Adventure Type Tags - Only for Adventures */}
          {showAdventureOnlyFilters && (
          <Grid item xs={12}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 1 }}>
                Adventure Types
                {filters.adventureTypeTags.length > 0 && (
                  <Chip
                    label={filters.adventureTypeTags.length}
                    size="small"
                    color="primary"
                    sx={{ ml: 2 }}
                  />
                )}
              </Typography>
              {tagsLoading ? (
                <Typography color="text.secondary">Loading tags...</Typography>
              ) : adventureTypeCategories.length === 0 ? (
                <Alert severity="info">No adventure type tags available</Alert>
              ) : (
                <>
                  {adventureTypeCategories
                    .filter((c) => c.isActive && c.tags && c.tags.some((t) => t.isActive))
                    .map((category) => (
                      <Accordion
                        key={category.id}
                        expanded={expandedAdventureTypeCategory === category.id}
                        onChange={(_, isExpanded) =>
                          setExpandedAdventureTypeCategory(isExpanded ? category.id : null)
                        }
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon sx={{ fontSize: 20 }} />}
                          sx={{
                            minHeight: '40px',
                            '&.Mui-expanded': { minHeight: '40px' },
                            '& .MuiAccordionSummary-content': {
                              my: 0.75,
                              '&.Mui-expanded': { my: 0.75 },
                            },
                          }}
                        >
                          <Box className="category-header">
                            <Box className="category-header-left">
                              <span className="category-icon" style={{ fontSize: '16px' }}>
                                {category.icon || '📁'}
                              </span>
                              <span
                                style={{ color: category.color, fontWeight: 500, fontSize: '0.875rem' }}
                              >
                                {category.displayName}
                              </span>
                            </Box>
                            <Box className="category-header-right">
                              <span
                                className="tag-count-badge"
                                style={{
                                  backgroundColor: category.color,
                                  color: 'white',
                                  fontSize: '0.7rem',
                                  padding: '2px 6px',
                                  minWidth: '20px',
                                  height: '18px',
                                }}
                              >
                                {category.tags?.filter((t) => t.isActive).length || 0}
                              </span>
                            </Box>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          {category.description && (
                            <Typography
                              variant="caption"
                              sx={{ mb: 2, display: 'block', color: '#666' }}
                            >
                              {category.description}
                            </Typography>
                          )}
                          <Box className="tag-chips-container">
                            {category.tags
                              ?.filter((t) => t.isActive)
                              .sort((a, b) => {
                                const orderDiff = a.sortOrder - b.sortOrder
                                return orderDiff !== 0
                                  ? orderDiff
                                  : a.displayName.localeCompare(b.displayName)
                              })
                              .map((tag) => {
                                const isSelected = filters.adventureTypeTags.includes(tag.id)
                                return (
                                  <Chip
                                    key={tag.id}
                                    label={
                                      <>
                                        {tag.icon && <span className="tag-icon">{tag.icon}</span>}
                                        <span className="tag-label">{tag.displayName}</span>
                                      </>
                                    }
                                    onClick={() => handleAdventureTypeTagToggle(tag.id)}
                                    variant={isSelected ? 'filled' : 'outlined'}
                                    size="small"
                                    sx={getTagChipStyle(category.color, isSelected)}
                                    title={tag.description}
                                    className="tag-chip"
                                  />
                                )
                              })}
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                </>
              )}
            </Box>
          </Grid>
          )}

          {/* Rating Range - Only for Adventures */}
          {showAdventureOnlyFilters && (
          <Grid item xs={12}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">
                  Adventure Rating
                  {(filters.minRating > 0 || filters.maxRating < 5) && (
                    <Chip
                      label={`${filters.minRating} - ${filters.maxRating} stars`}
                      size="small"
                      color="primary"
                      sx={{ ml: 2 }}
                    />
                  )}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ px: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Filter adventures by minimum and maximum rating
                  </Typography>
                  <Slider
                    value={ratingRange}
                    onChange={(_e, newValue) => setRatingRange(newValue as number[])}
                    valueLabelDisplay="on"
                    min={0}
                    max={5}
                    step={0.5}
                    marks={[
                      { value: 0, label: '0★' },
                      { value: 1, label: '1★' },
                      { value: 2, label: '2★' },
                      { value: 3, label: '3★' },
                      { value: 4, label: '4★' },
                      { value: 5, label: '5★' },
                    ]}
                    sx={{ mt: 4, mb: 2 }}
                  />
                </Box>
              </AccordionDetails>
            </Accordion>
          </Grid>
          )}

          <Grid item xs={12}>
            <Divider sx={{ my: { xs: 1, sm: 2 } }} />
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12}>
            <Box sx={{
              display: 'flex',
              gap: { xs: 1, sm: 2 },
              justifyContent: 'center',
              flexDirection: { xs: 'column', sm: 'row' }
            }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                sx={{ minWidth: { xs: '100%', sm: 200 } }}
              >
                {getSearchButtonText()}
              </Button>
              {hasActiveFilters && (
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<ClearIcon />}
                  onClick={handleClearFilters}
                  sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
                >
                  Clear Filters
                </Button>
              )}
            </Box>
          </Grid>

          {/* Active Filters Summary */}
          {hasActiveFilters && (
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{
                p: { xs: 1, sm: 1.5, md: 2 },
                bgcolor: 'background.default'
              }}>
                <Typography variant="subtitle2" gutterBottom>
                  Active Filters:
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {filters.entityTypes !== 15 && (
                    <Chip
                      label={`Searching: ${getSelectedEntityTypeNames()}`}
                      size="small"
                      color="info"
                    />
                  )}
                  {filters.keyword && (
                    <Chip label={`Keyword: ${filters.keyword}`} size="small" />
                  )}
                  {filters.categories.length > 0 && (
                    <Chip
                      label={`${filters.categories.length} Categories`}
                      size="small"
                      color="primary"
                    />
                  )}
                  {filters.interestTags.length > 0 && (
                    <Chip
                      label={`${filters.interestTags.length} Interest Tags`}
                      size="small"
                      color="primary"
                    />
                  )}
                  {filters.adventureTypeTags.length > 0 && (
                    <Chip
                      label={`${filters.adventureTypeTags.length} Adventure Types`}
                      size="small"
                      color="primary"
                    />
                  )}
                  {(filters.minRating > 0 || filters.maxRating < 5) && (
                    <Chip
                      label={`Rating: ${filters.minRating} - ${filters.maxRating}★`}
                      size="small"
                      color="primary"
                    />
                  )}
                </Stack>
              </Paper>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Save Search Dialog */}
      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Save Search</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            This search will be saved locally in your browser only. It will not be saved to your account.
          </Alert>
          <TextField
            autoFocus
            fullWidth
            label="Search Name"
            placeholder="e.g., Weekend Nature Adventures"
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSaveSearch()}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveSearch} variant="contained" disabled={!searchName.trim()}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Saved Searches Menu */}
      <Menu
        anchorEl={savedSearchesMenuAnchor}
        open={Boolean(savedSearchesMenuAnchor)}
        onClose={() => setSavedSearchesMenuAnchor(null)}
        PaperProps={{
          sx: { maxHeight: 400, width: 350 }
        }}
      >
        <MenuItem disabled>
          <Typography variant="caption" color="text.secondary">
            Saved searches (local to this browser)
          </Typography>
        </MenuItem>
        <Divider />
        {savedSearches.length === 0 ? (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              No saved searches yet
            </Typography>
          </MenuItem>
        ) : (
          savedSearches.map((search) => (
            <MenuItem
              key={search.id}
              onClick={() => handleLoadSearch(search)}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                pr: 1
              }}
            >
              <Box sx={{ flex: 1 }}>
                <ListItemText
                  primary={search.name}
                  secondary={new Date(search.savedAt).toLocaleDateString()}
                />
              </Box>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation()
                  handleDeleteSearch(search.id)
                }}
                sx={{ ml: 1 }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </MenuItem>
          ))
        )}
      </Menu>

      {/* Search Results */}
      {hasSearched && (
        <Box sx={{ mt: 4 }}>
          <Divider sx={{ mb: 3 }} />

          {isSearching ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h5">
                  Search Results
                  {totalResultCount > 0 && (
                    <Chip
                      label={`${totalResultCount} found`}
                      size="small"
                      color="primary"
                      sx={{ ml: 2 }}
                    />
                  )}
                </Typography>
              </Box>

              {totalResultCount === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                  <SearchIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    No Results Found
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Try adjusting your search criteria above to find more results.
                  </Typography>
                  {hasActiveFilters && (
                    <Button
                      variant="outlined"
                      startIcon={<ClearIcon />}
                      onClick={handleClearFilters}
                    >
                      Clear All Filters
                    </Button>
                  )}
                </Paper>
              ) : (
                <Box>
                  {/* Adventures */}
                  {(filters.entityTypes & SearchEntityType.Adventure) !== 0 && filteredResults.adventures.length > 0 && (
                    <>
                      {filteredResults.adventures.map((adventure) => (
                        <AdventureResultCard key={`adv-${adventure.id}`} adventure={adventure} />
                      ))}
                    </>
                  )}

                  {/* Users */}
                  {(filters.entityTypes & SearchEntityType.User) !== 0 && filteredResults.users.length > 0 && (
                    <>
                      {filteredResults.users.map((user) => (
                        <UserResultCard key={`user-${user.id}`} user={user} />
                      ))}
                    </>
                  )}

                  {/* Groups */}
                  {(filters.entityTypes & SearchEntityType.Group) !== 0 && filteredResults.groups.length > 0 && (
                    <>
                      {filteredResults.groups.map((group) => (
                        <GroupResultCard key={`group-${group.id}`} group={group} />
                      ))}
                    </>
                  )}

                  {/* Collections */}
                  {(filters.entityTypes & SearchEntityType.Collections) !== 0 && filteredResults.collections.length > 0 && (
                    <>
                      {filteredResults.collections.map((collection) => (
                        <CollectionResultCard key={`col-${collection.id}`} collection={collection} />
                      ))}
                    </>
                  )}
                </Box>
              )}
            </>
          )}
        </Box>
      )}
    </Container>
  )
}
